
import { useState, useEffect, type FormEvent } from 'react';
import { motion } from 'framer-motion';

import svgPaths from "../imports/svg-n7w9hotcin";
import { fetchAvailableTools, setSessionContext, summarizeSession, renameSessionByAI } from '../api';
import type { AvailableToolsListType, completionFunctionalToolsCallsLogType, SessionContextType, PageNameOption, SessionSummary, SessionRenameOptions, SessionUsageInfo } from '../api.types';
import { PAGE_NAME_OPTIONS } from '../api.types';
import { useSession } from './SessionDropdown';

type SessionContextDisplay = SessionContextType;

type Props = {
  isMobile?: boolean;
  onClose?: () => void;
  lastToolsCalls?: completionFunctionalToolsCallsLogType[];
  totalTokens?: number;
  allowedTools?: string[];
  onAllowedToolsChange?: (tools: string[]) => void;
  sessionCurrentContext?: SessionContextDisplay | null;
  sessionContextHistory?: SessionContextDisplay[];
  sessionSummary?: SessionSummary | null;
  onSummaryUpdate?: (summary: SessionSummary | null) => void;
  sessionRenameOptions?: SessionRenameOptions | null;
  onRenameOptionsUpdate?: (renameOptions: SessionRenameOptions | null) => void;
  sessionUsage?: SessionUsageInfo | null;
};

export default function BriefSidebar({
  isMobile = false,
  onClose,
  lastToolsCalls = [],
  totalTokens,
  allowedTools = [],
  onAllowedToolsChange,
  sessionCurrentContext = null,
  sessionContextHistory = [],
  sessionSummary = null,
  onSummaryUpdate,
  sessionRenameOptions = null,
  onRenameOptionsUpdate,
  sessionUsage = null
}: Props) {
  const [availableTools, setAvailableTools] = useState<AvailableToolsListType[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set(allowedTools));
  const { selectedSession, setSelectedSession } = useSession();
  const [websiteId, setWebsiteId] = useState('');
  const [pageName, setPageName] = useState<PageNameOption | ''>('');
  const [contextSubmitting, setContextSubmitting] = useState(false);
  const [contextFeedback, setContextFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [summarySubmitting, setSummarySubmitting] = useState(false);
  const [summaryFeedback, setSummaryFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameFeedback, setRenameFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [renamedSessionName, setRenamedSessionName] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || '';
    if (!token) return;
    fetchAvailableTools(token).then(setAvailableTools).catch(() => setAvailableTools([]));
  }, []);

  // Keep internal selection in sync with parent-provided allowedTools
  useEffect(() => {
    setSelectedTools(new Set(allowedTools));
  }, [allowedTools]);

  useEffect(() => {
    setWebsiteId('');
    setPageName('');
    setContextFeedback(null);
    setSummaryFeedback(null);
    setRenameFeedback(null);
    setRenamedSessionName(null);
  }, [selectedSession?.id]);

  // When tools are fetched first time and parent has no selection, default to all
  useEffect(() => {
    if (availableTools.length > 0 && allowedTools.length === 0) {
      const all = availableTools.map(t => t.name);
      setSelectedTools(new Set(all));
      onAllowedToolsChange && onAllowedToolsChange(all);
    }
  }, [availableTools]);

  const toggleTool = (toolName: string) => {
    const next = new Set(selectedTools);
    if (next.has(toolName)) {
      next.delete(toolName);
    } else {
      next.add(toolName);
    }
    setSelectedTools(next);
    onAllowedToolsChange && onAllowedToolsChange(Array.from(next));
  };

  const displayTokens = totalTokens;
  const isContextDisabled = !selectedSession || contextSubmitting;
  const hasInput = websiteId.trim() !== '' || pageName !== '';
  const hasExistingContext = Boolean(sessionCurrentContext?.websiteId || sessionCurrentContext?.pageName);
  const canSubmitContext = Boolean(selectedSession && !contextSubmitting && (hasInput || hasExistingContext));

  const formatContextValue = (value: string | PageNameOption | '' | null | undefined) => {
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      return '—';
    }
    return value;
  };

  const handleContextSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSession || !canSubmitContext) {
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setContextFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }

    try {
      setContextSubmitting(true);
      await setSessionContext(token, selectedSession.id, {
        websiteId: websiteId.trim(),
        pageName,
      });
      setContextFeedback({ type: 'success', message: 'Session context saved.' });
    } catch (error) {
      setContextFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save session context.',
      });
    } finally {
      setContextSubmitting(false);
    }
  };

  const handleSummarize = async () => {
    if (!selectedSession || !selectedSession.id) {
      setSummaryFeedback({ type: 'error', message: 'Select a session first.' });
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setSummaryFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }
    try {
      setSummarySubmitting(true);
      setSummaryFeedback(null);
      const summary = await summarizeSession(token, selectedSession.id);
      onSummaryUpdate && onSummaryUpdate(summary);
      setSummaryFeedback({ type: 'success', message: 'Summary updated.' });
    } catch (error) {
      setSummaryFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to summarize session.'
      });
    } finally {
      setSummarySubmitting(false);
    }
  };

  const formatSummaryTimestamp = (timestamp?: string) => {
    if (!timestamp) {
      return '—';
    }
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString();
  };

  const handleRename = async (force: boolean = false) => {
    if (!selectedSession || !selectedSession.id) {
      setRenameFeedback({ type: 'error', message: 'Select a session first.' });
      return;
    }
    const token = localStorage.getItem('authToken') || '';
    if (!token) {
      setRenameFeedback({ type: 'error', message: 'Authorization token is missing.' });
      return;
    }
    try {
      setRenameSubmitting(true);
      setRenameFeedback(null);
      const { renameOptions, sessionName } = await renameSessionByAI(token, selectedSession.id, force);
      onRenameOptionsUpdate && onRenameOptionsUpdate(renameOptions || null);
      setRenamedSessionName(sessionName);
      // Update selected session name in context
      setSelectedSession({ id: selectedSession.id, name: sessionName });
      setRenameFeedback({ type: 'success', message: force ? 'Session renamed (forced).' : 'Session renamed.' });
    } catch (error) {
      setRenameFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to rename session.'
      });
    } finally {
      setRenameSubmitting(false);
    }
  };

  return (
    <div className={`bg-[#f6f8ff] h-full ${!isMobile ? 'rounded-tl-[16px]' : 'mobile-brief-sidebar'} overflow-hidden`}>
      {isMobile && (
        <div className="relative">
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto my-2" />
          {onClose && (
            <button onClick={onClose} className="absolute right-4 top-0 p-2 text-gray-500 hover:text-gray-700">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
              </svg>
            </button>
          )}
        </div>
      )}

      <div className="h-0 w-full border-b border-[#EDF0F5] mt-[15px]"></div>

      <div className={`pt-8 px-6 pb-8 overflow-y-auto h-full custom-scrollbar ${!isMobile ? 'pt-14 px-8' : ''}`}>
        {/* Title */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="size-10">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
                <g>
                  <g>
                    <path d={svgPaths.p1fade100} fill="#165BFB" />
                    <path d={svgPaths.p230cf780} fill="#165BFB" />
                    <path d={svgPaths.p26295800} fill="#165BFB" />
                  </g>
                </g>
              </svg>
            </div>
            <div className="font-bold text-[#165bfb] text-[40px]">
              <p className={`leading-[36px] ${isMobile ? 'text-[32px] leading-[40px]' : ''}`}>Functional Tools</p>
            </div>
          </div>
        </div>

        {/* Block 1: Session context */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Session context</p>
          </div>
          <form className="flex flex-col gap-4" onSubmit={handleContextSubmit}>
            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a7a7a]" htmlFor="session-context-website">
                Website ID
              </label>
              <input
                id="session-context-website"
                type="text"
                value={websiteId}
                disabled={isContextDisabled}
                onChange={(event) => {
                  setWebsiteId(event.target.value);
                  setContextFeedback(null);
                }}
                placeholder={selectedSession ? 'Enter website id' : 'Select a session first'}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[12px] text-[#7a7a7a]" htmlFor="session-context-page">
                Page name
              </label>
              <select
                id="session-context-page"
                value={pageName}
                disabled={isContextDisabled}
                onChange={(event) => {
                  setPageName(event.target.value as PageNameOption | '');
                  setContextFeedback(null);
                }}
                className="w-full border border-[#EDF0F5] h-11 px-4 rounded-lg focus:border-[#165BFB] focus:ring-1 focus:ring-[#165BFB] transition-all outline-none disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF] bg-white"
              >
                <option value="">{selectedSession ? 'Not set' : 'Select a session first'}</option>
                {PAGE_NAME_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={!canSubmitContext}
              className={`h-11 rounded-lg font-semibold transition-colors ${
                canSubmitContext
                  ? 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                  : 'bg-[#cdd7ff] text-white cursor-not-allowed'
              }`}
            >
              {contextSubmitting ? 'Saving...' : 'Save context'}
            </button>
            {contextFeedback && (
              <div
                className={`text-[12px] ${
                  contextFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'
                }`}
              >
                {contextFeedback.message}
              </div>
            )}
          </form>
        </motion.div>

        {/* Block 2: Session data */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.32 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Session data</p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
              <div className="text-[12px] text-[#7a7a7a] uppercase tracking-wide mb-2">Current context</div>
              {sessionCurrentContext ? (
                <div className="text-sm text-[#111117] space-y-1">
                  <div><span className="font-semibold text-[#464C5C]">Website ID:</span> {formatContextValue(sessionCurrentContext.websiteId)}</div>
                  <div><span className="font-semibold text-[#464C5C]">Page name:</span> {formatContextValue(sessionCurrentContext.pageName)}</div>
                </div>
              ) : (
                <div className="text-sm text-[#6B7280]">Session context is empty.</div>
              )}
            </div>
            <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
              <div className="text-[12px] text-[#7a7a7a] uppercase tracking-wide mb-2">Context history</div>
              {sessionContextHistory.length > 0 ? (
                <ul className="space-y-3">
                  {sessionContextHistory.map((contextItem, index) => (
                    <li key={`${contextItem.websiteId ?? 'null'}-${contextItem.pageName ?? 'null'}-${index}`} className="text-sm text-[#111117] bg-white border border-[#e8edf7] rounded-lg p-3">
                      <div><span className="font-semibold text-[#464C5C]">Website ID:</span> {formatContextValue(contextItem.websiteId)}</div>
                      <div><span className="font-semibold text-[#464C5C]">Page name:</span> {formatContextValue(contextItem.pageName)}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-[#6B7280]">No context history yet.</div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Block 3: Session Rename */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.325 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Session Rename</p>
          </div>
          <div className="flex flex-col gap-4">
            {sessionRenameOptions || renamedSessionName ? (
              <div className="space-y-3">
                <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                  <div className="text-xs text-[#6B7280] space-y-1">
                    <div><span className="font-semibold text-[#464C5C]">Session name:</span> {renamedSessionName || selectedSession?.name || '—'}</div>
                    {sessionRenameOptions && (
                      <>
                        <div><span className="font-semibold text-[#464C5C]">Updated:</span> {formatSummaryTimestamp(sessionRenameOptions.timestamp)}</div>
                        <div><span className="font-semibold text-[#464C5C]">Messages used:</span> {sessionRenameOptions.userMessagesUsed}</div>
                        <div><span className="font-semibold text-[#464C5C]">Regenerations:</span> {sessionRenameOptions.regenerationCount}</div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#6B7280]">Session name not generated yet.</div>
            )}
            {renameFeedback && (
              <div className={`text-[12px] ${renameFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {renameFeedback.message}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleRename(false)}
                disabled={!selectedSession || renameSubmitting}
                className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                  !selectedSession || renameSubmitting
                    ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                    : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                }`}
              >
                {renameSubmitting ? 'Renaming…' : 'Rename'}
              </button>
              <button
                type="button"
                onClick={() => handleRename(true)}
                disabled={!selectedSession || renameSubmitting}
                className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                  !selectedSession || renameSubmitting
                    ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                    : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
                }`}
              >
                {renameSubmitting ? 'Renaming…' : 'Force Rename'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Block 4: Summary */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.33 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Summary</p>
          </div>
          <div className="flex flex-col gap-4">
            {sessionSummary ? (
              <div className="space-y-3">
                <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5] max-h-48 overflow-auto whitespace-pre-line text-sm text-[#111117]">
                  {sessionSummary.text}
                </div>
                <div className="text-xs text-[#6B7280]">
                  <div><span className="font-semibold text-[#464C5C]">Updated:</span> {formatSummaryTimestamp(sessionSummary.timestamp)}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#6B7280]">Summary is not set yet.</div>
            )}
            {summaryFeedback && (
              <div className={`text-[12px] ${summaryFeedback.type === 'success' ? 'text-[#166534]' : 'text-[#DC2626]'}`}>
                {summaryFeedback.message}
              </div>
            )}
            <button
              type="button"
              onClick={handleSummarize}
              disabled={!selectedSession || summarySubmitting}
              className={`h-11 rounded-lg font-semibold transition-colors ${
                !selectedSession || summarySubmitting
                  ? 'bg-[#cdd7ff] text-white cursor-not-allowed'
                  : 'bg-[#165bfb] text-white hover:bg-[#1246d7] active:bg-[#0d38b0]'
              }`}
            >
              {summarySubmitting ? 'Summarizing…' : 'Summarize'}
            </button>
          </div>
        </motion.div>

        {/* Block 5: Available Tools */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.34 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Available Tools</p>
          </div>
          <div className="space-y-3">
            {availableTools.length === 0 && (
              <div className="text-[14px] text-[#7a7a7a]">No tools available</div>
            )}
            {availableTools.map((tool) => (
              <label key={tool.name} className="flex items-center gap-3 bg-[#f9fbff] p-3 rounded-lg border border-[#edf0f5]">
                <input
                  type="checkbox"
                  checked={selectedTools.has(tool.name)}
                  onChange={() => toggleTool(tool.name)}
                  className="size-4 accent-[#165bfb]"
                />
                <div>
                  <div className="text-[14px] font-medium text-[#111117] leading-[18px]">{tool.name}</div>
                  <div className="text-[12px] text-[#7a7a7a] leading-[16px]">{tool.description}</div>
                </div>
              </label>
            ))}
          </div>
        </motion.div>

        {/* Block 6: Last Tools Calls */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Last Tools Calls</p>
          </div>

          {(!lastToolsCalls || lastToolsCalls.length === 0) && (
            <div className="text-[14px] text-[#7a7a7a]">No tool calls yet</div>
          )}

          {lastToolsCalls && lastToolsCalls.length > 0 && (
            <div className="space-y-4">
              {lastToolsCalls.map((call) => (
                <div key={call.id} className="rounded-xl border border-[#edf0f5] p-4 bg-[#f9fbff]">

                  {/*<div className="flex items-center justify-between mb-2">*/}
                  {/*  <div className="text-[14px] font-semibold text-[#111117]">{call.name}</div>*/}
                  {/*</div>*/}

                  <div className="grid gap-2">
                    <div>
                      <div className="text-[12px] text-[#7a7a7a] mb-1">Name</div>
                      <pre className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">{call.name}</pre>
                    </div>
                    <div>
                      <div className="text-[12px] text-[#7a7a7a] mb-1">ID</div>
                      <pre className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">{call.id}</pre>
                    </div>
                    <div>
                      <div className="text-[12px] text-[#7a7a7a] mb-1">Arguments</div>
                      <pre className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">{call.arguments}</pre>
                    </div>
                    <div>
                      <div className="text-[12px] text-[#7a7a7a] mb-1">Result</div>
                      <pre className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">{call.result}</pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Block 7: Statistics */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5 mb-8" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.38 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Statistics</p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="bg-[#f7f9ff] rounded-xl p-3 border border-[#edf0f5]">
              <div className="text-[12px] text-[#7a7a7a] mb-1">Tokens used</div>
              <div className="text-[12px] p-2 bg-white rounded border border-[#e8edf7] overflow-x-auto whitespace-pre-wrap break-words">
                {sessionUsage ? (
                  <div className="space-y-1">
                    <div><span className="font-semibold text-[#464C5C]">Input:</span> {sessionUsage.inputTokensUsed.toLocaleString()}</div>
                    <div><span className="font-semibold text-[#464C5C]">Output:</span> {sessionUsage.outputTokensUsed.toLocaleString()}</div>
                    <div><span className="font-semibold text-[#464C5C]">Total:</span> {(sessionUsage.inputTokensUsed + sessionUsage.outputTokensUsed).toLocaleString()}</div>
                  </div>
                ) : (
                  displayTokens || 'N/A'
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Block 8: Limits */}
        <motion.div className="bg-white rounded-2xl border-2 border-[#edf0f5] p-5" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}>
          <div className="font-bold text-[18px] text-[#111117] mb-4">
            <p className="leading-[22px]">Limits</p>
          </div>
          {sessionUsage ? (
            <div className="flex flex-col gap-4">
              {/* Limit Class Badge */}
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[#7a7a7a]">Limit Class:</span>
                <span className="px-2.5 py-1 text-[12px] font-semibold rounded-full bg-[#165bfb] text-white">
                  {sessionUsage.limitClass}
                </span>
              </div>

              {/* Messages Usage */}
              <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a7a7a] uppercase tracking-wide">Messages</span>
                  <span className="text-[12px] font-semibold text-[#111117]">
                    {sessionUsage.messagesUsed} / {sessionUsage.maxMessages}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.messagesUsed / sessionUsage.maxMessages > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.messagesUsed / sessionUsage.maxMessages > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.messagesUsed / sessionUsage.maxMessages) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.messagesRemaining} remaining
                </div>
              </div>

              {/* Input Tokens Usage */}
              <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a7a7a] uppercase tracking-wide">Input Tokens</span>
                  <span className="text-[12px] font-semibold text-[#111117]">
                    {sessionUsage.inputTokensUsed.toLocaleString()} / {sessionUsage.maxInputTokens.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.inputTokensUsed / sessionUsage.maxInputTokens) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.inputTokensRemaining.toLocaleString()} remaining
                </div>
              </div>

              {/* Output Tokens Usage */}
              <div className="bg-[#f7f9ff] rounded-xl p-4 border border-[#edf0f5]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[12px] text-[#7a7a7a] uppercase tracking-wide">Output Tokens</span>
                  <span className="text-[12px] font-semibold text-[#111117]">
                    {sessionUsage.outputTokensUsed.toLocaleString()} / {sessionUsage.maxOutputTokens.toLocaleString()}
                  </span>
                </div>
                <div className="w-full h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens > 0.9
                        ? 'bg-[#ef4444]'
                        : sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens > 0.7
                        ? 'bg-[#f59e0b]'
                        : 'bg-[#22c55e]'
                    }`}
                    style={{ width: `${Math.min(100, (sessionUsage.outputTokensUsed / sessionUsage.maxOutputTokens) * 100)}%` }}
                  />
                </div>
                <div className="text-[10px] text-[#6b7280] mt-1">
                  {sessionUsage.outputTokensRemaining.toLocaleString()} remaining
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[14px] text-[#7a7a7a]">No limits data available. Select a session to view limits.</div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
